<template>
  <form @submit.prevent>
    <div class="flex justify-between counter-controls-top">
      <span :class="{'text-sm': true, 'text-danger': isMessageExceededLimit}">
        {{ $t('CharactersMessageCountMsg', {
        charactersCount: this.charactersCount,
        unicode: this.hasUnicodeCharacters
          ? this.$options.filters.lowercase(`(${this.$t('$General.Unicode')})`) : '',
        messagesCount: this.messagesCount,
        }) }}
      </span>

      <div class="flex flex-wrap gap-x-5">
        <s-m-s-text-box-placeholders-menu
          v-if="!disabled"
          :all-contact-attrs="allContactAttrs"
          :is-waiting-list-message="isWaitingListMessage"
          @insert="insertShorthand($event)"
          @redirected="$emit('redirected')"/>

        <vs-dropdown
          ref="emojiDropdown"
          v-if="!disabled"
          vs-custom-content
          vs-trigger-click

          class="cursor-pointer emoji-button">
          <a
            class="flex items-center">
            <feather-icon
              icon="SmileIcon"
              svgClasses="h-5 w-5" />
          </a>
          <vs-dropdown-menu class="dropdown-login emojis">
            <div @click.stop="handleEmojiPickerClick">
              <VEmojiPicker @select="selectEmoji"/>
            </div>
          </vs-dropdown-menu>
        </vs-dropdown>
      </div>
    </div>

    <div
      class="text-wrapper mt-1">
      <s-m-s-text-box-editor
        ref="smsTextBox"
        v-model="localMessage"
        :interpolations="localInterpolations"
        :error="errors.has($tc('$General.Message'))"
        :label="`${$tc('$General.Message')}*`"
        @blur="editorBlur"
      />
      <span
        v-show="errors.has($tc('$General.Message'))"
        class="text-danger">
      {{ errors.first($tc('$General.Message')) }}
      </span>

      <vs-input
        v-model="localMessage"
        type="hidden"
        :name="$tc('$General.Message')"
        v-validate="'required'"
        data-vv-validate-on="blur|input"/>
    </div>
  </form>
</template>

<script>
import 'vue-context/src/sass/vue-context.scss';
import { mapActions } from 'vuex';
import enums from '@/enums';

// Mixins
import formValidationExtensions from '@/views/modules/_mixins/formValidationExtensions';
import SMSTextBoxPlaceholdersMenu from '@/views/modules/_components/SMSTextBoxPlaceholdersMenu.vue';
import SMSTextBoxEditor from '@/views/modules/_components/SMSTextBoxEditor.vue';

/**
 * Component of text area with emojis, attributes interpolations and count danger
 *
 * @module views/modules/components/SMSTextBox
 * @author Dilan Useche <dilan8810@gmail.com>
 */
export default {
  name: 'SMSTextBox',
  i18n: {
    messages: {
      en: {
        InsertPlaceholders: 'Insert placeholders',
        InsertCustomFields: 'Insert contact attributes',
        AddEmoji: 'Add emoji',
        InvalidContactAttributesTitle: 'Invalid contact attributes:',
        CharactersMessageCountMsg: '{charactersCount} characters {unicode} / {messagesCount} SMS',
      },
    },
  },
  components: {
    SMSTextBoxPlaceholdersMenu,
    SMSTextBoxEditor,
  },
  mixins: [formValidationExtensions],
  props: {
    value: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    attributesFromContacts: {
      type: Array,
      required: false,
    },
    hasInterpolations: {
      type: Boolean,
      required: false,
      default: false,
    },
    interpolations: {
      type: Array,
      required: false,
      default() {
        return [];
      },
      validator(interpolations) {
        return interpolations.every((interpolation) => typeof interpolation === 'object'
          && interpolation !== null
          && 'shorthand' in interpolation
          && typeof interpolation.shorthand === 'string'
          && 'attribute' in interpolation
          && typeof interpolation.attribute === 'string');
      },
    },
    messageType: {
      type: String,
      required: false,
      validator(type) {
        if (type === '') return true;
        return Object.values(enums.Campaign.MessageType).includes(type);
      },
    },
    messageLengthOffset: {
      type: Number,
      required: false,
      default: 0,
    },
    minHeight: {
      type: String,
      default: '10rem',
    },
    isWaitingListMessage: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      localMessage: this.value,
      localHasInterpolations: this.hasInterpolations,
      localInterpolations: this.interpolations,
      cursorPosition: 0,
      defaultContactAttrs: [
        {
          name: this.$enums.Campaign.DefaultPlaceholders.FULL_NAME,
          shorthand: `[${this.$enums.Campaign.DefaultPlaceholders.FULL_NAME}]`,
          alias: this.$t('$General.FullName'),
          attr: this.$enums.Campaign.DefaultPlaceholdersAttributeName.FULL_NAME,
          type: this.$enums.Attributes.Type.DEFAULT,
        },
        {
          name: this.$enums.Campaign.DefaultPlaceholders.FIRST_NAME,
          shorthand: `[${this.$enums.Campaign.DefaultPlaceholders.FIRST_NAME}]`,
          alias: this.$t('$General.FirstName'),
          attr: this.$enums.Campaign.DefaultPlaceholdersAttributeName.FIRST_NAME,
          type: this.$enums.Attributes.Type.DEFAULT,
        },
        {
          name: this.$enums.Campaign.DefaultPlaceholders.LAST_NAME,
          shorthand: `[${this.$enums.Campaign.DefaultPlaceholders.LAST_NAME}]`,
          alias: this.$t('$General.LastName'),
          attr: this.$enums.Campaign.DefaultPlaceholdersAttributeName.LAST_NAME,
          type: this.$enums.Attributes.Type.DEFAULT,
        },
        {
          name: this.$enums.Campaign.DefaultPlaceholders.EMAIL,
          shorthand: `[${this.$enums.Campaign.DefaultPlaceholders.EMAIL}]`,
          alias: this.$tc('$General.Email'),
          attr: this.$enums.Campaign.DefaultPlaceholdersAttributeName.EMAIL,
          type: this.$enums.Attributes.Type.DEFAULT,
        },
        {
          name: this.$enums.Campaign.DefaultPlaceholders.PHONE,
          shorthand: `[${this.$enums.Campaign.DefaultPlaceholders.PHONE}]`,
          alias: this.$tc('$General.Phone'),
          attr: this.$enums.Campaign.DefaultPlaceholdersAttributeName.PHONE,
          type: this.$enums.Attributes.Type.DEFAULT,
        },
      ],
      attributesFromContactsLocal: [],
    };
  },
  computed: {
    isValid() {
      return this.validate();
    },
    hasUnicodeCharacters() {
      return /[^A-Za-z0-9 \r\n@£$¥èéùìòÇØøÅå\u0394_\u03A6\u0393\u039B\u03A9\u03A0\u03A8\u03A3\u0398\u039EÆæßÉ!"#¤%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\[~\]|€]/g.test(this.localMessage);
    },
    specialCharactersCount() {
      const result = this.localMessage.match(/[\^{}\\[~\]|€]/g);
      return result !== null ? result.length : 0;
    },
    charactersCount() {
      return this.localMessage.length + this.specialCharactersCount + this.messageLengthOffset;
    },
    gsmMessagesCount() {
      let count = 1;
      let found = false;

      [459, 306, 160].forEach((limit, index) => {
        if (this.charactersCount > limit && !found) {
          count = 4 - index;
          found = true;
        }
      });

      return count;
    },
    unicodeMessagesCount() {
      let count = 1;
      let found = false;

      [201, 134, 70].forEach((limit, index) => {
        if (this.charactersCount > limit && !found) {
          count = 4 - index;
          found = true;
        }
      });

      return count;
    },
    messagesCount() {
      return this.hasUnicodeCharacters
        ? this.unicodeMessagesCount
        : this.gsmMessagesCount;
    },
    messageMaxLength() {
      return this.hasUnicodeCharacters ? 268 : 612;
    },
    isMessageExceededLimit() {
      return this.charactersCount > this.messageMaxLength;
    },
    allContactAttrs() {
      const aliases = [];
      return [
        ...this.defaultContactAttrs,
        ...this.attributesFromContactsLocal.map((attr) => {
          let alias = attr.name;
          const aliasCount = aliases.filter((a) => a === alias).length;
          alias = aliasCount > 0 ? `${alias}${aliasCount + 1}` : alias;
          aliases.push(alias);

          return {
            name: attr.name,
            shorthand: `[${alias}]`,
            alias,
            attr: attr.id,
            type: attr.type,
          };
        }),
      ];
    },
  },
  watch: {
    allContactAttrs() {
      this.validateInterpolations();
    },
    attributesFromContacts() {
      this.attributesFromContactsLocal = this.attributesFromContacts;
    },
    localHasInterpolations() {
      this.$emit('update:has-interpolations', this.localHasInterpolations);
    },
    localInterpolations() {
      this.$emit('update:interpolations', this.localInterpolations);
    },
    value() {
      this.localMessage = this.value;
    },
    localMessage() {
      this.updateValue();
    },
    isValid: {
      immediate: true,
      handler(value) {
        this.$emit('state-change', value);
      },
    },
  },
  created() {
    console.log('SMSTexBox created');
    this.initComponent();
  },
  methods: {
    ...mapActions({
      fetchAllAttributes: 'attribute/fetchAllAttributes',
    }),
    async initComponent() {
      if (!this.attributesFromContacts) {
        await this.fetchContactsAttributes();
      } else {
        this.attributesFromContactsLocal = this.attributesFromContacts;
      }

      this.validateInterpolations();
    },
    async fetchContactsAttributes() {
      const resp = await this.fetchAllAttributes({});
      this.attributesFromContactsLocal = resp.data;
    },
    updateValue() {
      this.$emit('input', this.localMessage);
      this.validateInterpolations();
    },
    validateInterpolations() {
      const tokens = this.localMessage.match(/\[([^\]]*)]/g);
      const interpolations = [];

      if (tokens !== null) {
        tokens.forEach((token) => {
          const attr = token.match(/\[\s*(.*?)\s*\]/)[1].trim();

          // Try to validate as contact attribute first
          const contactInterpolation = this.validateContactAttribute(token, attr);
          if (contactInterpolation) {
            interpolations.push(contactInterpolation);
            return;
          }

          // Try special placeholders
          const specialInterpolation = this.validateSpecialPlaceholder(token, attr);
          if (specialInterpolation) {
            interpolations.push(specialInterpolation);
            return;
          }

          // Try waitlist placeholders
          const waitlistInterpolation = this.validateWaitlistPlaceholder(token, attr);
          if (waitlistInterpolation) {
            interpolations.push(waitlistInterpolation);
            return;
          }

          // Try business placeholders
          const businessInterpolation = this.validateBusinessPlaceholder(token, attr);
          if (businessInterpolation) {
            interpolations.push(businessInterpolation);
          }
        });

        this.localHasInterpolations = interpolations.length > 0;
        this.localInterpolations = interpolations;
      } else {
        this.localHasInterpolations = false;
        this.localInterpolations = [];
      }
    },
    validateContactAttribute(token, attr) {
      // Build list of all possible identifiers (name, alias)
      const attrList = this.allContactAttrs
        .map((a) => [a.name, a.alias].filter(Boolean))
        .flat()
        .toString()
        .replace(/,/g, '|');
      const regex = new RegExp(`^\\s*(${attrList})\\s*$`);

      if (!regex.test(attr)) {
        return null;
      }

      // Find by name or alias
      const contactAttr = this.allContactAttrs.find(
        (a) => a.name === attr || (a.alias && a.alias === attr),
      );

      if (!contactAttr) {
        return null;
      }

      return {
        shorthand: token,
        attribute: contactAttr.attr,
        type: contactAttr.type,
        name: contactAttr.name,
      };
    },
    validateSpecialPlaceholder(token, attr) {
      const specialPlaceholders = Object.values(this.$enums.Campaign.SpecialPlaceholders);

      if (!specialPlaceholders.includes(attr)) {
        return null;
      }

      return {
        shorthand: token,
        attribute: attr,
        type: this.$enums.Attributes.Type.SPECIAL,
        name: attr,
      };
    },
    validateWaitlistPlaceholder(token, attr) {
      const waitlistPlaceholders = Object.values(this.$enums.Campaign.WaitListPlaceholders);

      if (!waitlistPlaceholders.includes(attr)) {
        return null;
      }

      return {
        shorthand: token,
        attribute: attr,
        type: this.$enums.Attributes.Type.WAITLIST,
        name: attr,
      };
    },
    validateBusinessPlaceholder(token, attr) {
      const businessPlaceholderEntry = Object.entries(this.$enums.Campaign.BusinessPlaceholders)
        .find(([key, label]) => label === attr);

      if (!businessPlaceholderEntry) {
        return null;
      }

      const [key, label] = businessPlaceholderEntry;
      const attributeName = this.$enums.Campaign.BusinessPlaceholdersAttributeName[key];

      return {
        shorthand: token,
        attribute: attributeName,
        type: this.$enums.Attributes.Type.BUSINESS,
        name: label,
      };
    },
    insertShorthand(attr) {
      if (attr.attribute === this.$enums.Campaign.SpecialPlaceholders.REVIEW_LINK) {
        if (this.messageType && this.messageType === this.$enums.Campaign.MessageType.MARKETING) {
          this.$emit('review-link-on-marketing');
        } else {
          this.insertPlaceholder(attr.shorthand, attr.name);
        }
      } else {
        this.insertPlaceholder(attr.shorthand, attr.name);
      }
    },
    insertPlaceholder(shorthand, name) {
      // Use the editor's direct insertion method
      if (this.$refs.smsTextBox && this.$refs.smsTextBox.insertPlaceholderAtCursor) {
        this.$refs.smsTextBox.insertPlaceholderAtCursor(shorthand, name);
        // The editor will emit the updated value through handleChange
      }
    },
    insertText(text) {
      // Fallback for non-placeholder text insertion (like emojis)
      // Get the current cursor position from the editor
      let cursorPos = this.cursorPosition;
      if (this.$refs.smsTextBox && this.$refs.smsTextBox.getCurrentCursorPosition) {
        cursorPos = this.$refs.smsTextBox.getCurrentCursorPosition();
      }

      const beforeMsg = this.localMessage.substring(0, cursorPos);
      const afterMsg = this.localMessage.substring(cursorPos);

      // todo: remove later if we will not use this anymore
      const needsSpaceBefore = beforeMsg.length > 0 && beforeMsg.slice(-1) !== ' ';
      const needsSpaceAfter = afterMsg.length === 0 || (afterMsg.length > 0 && afterMsg[0] !== ' ');

      // eslint-disable-next-line max-len
      // this.localMessage = `${beforeMsg}${needsSpaceBefore ? ' ' : ''}${text}${needsSpaceAfter ? ' ' : ''}${afterMsg}`;
      this.localMessage = `${beforeMsg}${text}${afterMsg}`;

      // Update cursor position to be right after the inserted text
      this.cursorPosition = beforeMsg.length + text.length;

      this.updateValue();
    },
    deleteInterpolation(attribute) {
      const found = this.localInterpolations.some(
        (interpolation) => interpolation.attribute === attribute,
      );

      if (found) {
        const regex = new RegExp(`(\\[)\\s*(${attribute})\\s*(\\])`, 'g');
        this.localMessage = this.localMessage.replace(regex, '');
        this.updateValue();
      }
    },
    handleEmojiPickerClick(event) {
      // Prevent clicks inside emoji picker from closing the dropdown
      // This allows interacting with search bar, categories, etc.
      // The dropdown will only close when selectEmoji is called
      event.stopPropagation();
    },
    selectEmoji(emoji) {
      // Use the editor's insertTextAtCursor method for consistent behavior with placeholders
      if (this.$refs.smsTextBox && this.$refs.smsTextBox.insertTextAtCursor) {
        this.$refs.smsTextBox.insertTextAtCursor(emoji.data);
      } else {
        // Fallback to old method if new method not available
        this.insertText(emoji.data);
      }

      // Close emoji dropdown after inserting emoji
      // Vuesax dropdowns close when clicking outside, so we simulate a body click
      this.$nextTick(() => {
        document.body.click();
      });
    },
    editorBlur() {
      // Validate message when editor loses focus
      this.$validator.validate(this.$tc('$General.Message'));
    },
    validate() {
      return !this.errors.has(this.$tc('$General.Message'))
        && !this.isMessageExceededLimit
        && this.localMessage.length > 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.con-vs-dropdown--menu {
  z-index: 54000 !important;
}

.emoji-button {
  height: 1.5rem;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 50 !important;
}

.counter-controls-top {
  flex-wrap: wrap;
  margin-bottom: 1rem;
  span.text-sm {
    padding-left: 2px;
  }
}

.v-context__sub {
  > a:after {
      content: "\25B6";
      font-size: 12px;
      padding-top: 0.15rem;
  }
}

</style>
